import { useRef, useEffect } from 'react'
import { HOST } from '@/config/consts'
import { positionToRank, rankToBadgeUrl, rankToTitle } from '@/features/points/utils/rank'

interface ImageEditorProps {
  imageUrl: string
  // optional inputs; if not provided we draw demo values
  points?: number
  rank?: number
  onImageGenerated: (imageDataUrl: string) => void
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function drawRoundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.min(r, h / 2, w / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + w - radius, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
  ctx.lineTo(x + w, y + h - radius)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
  ctx.lineTo(x + radius, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

export function PointsImageEditor({ imageUrl, points, rank, onImageGenerated }: ImageEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const bg = new Image()
    bg.src = imageUrl
    bg.onload = () => {
      canvas.width = bg.width
      canvas.height = bg.height

      // draw background
      ctx.drawImage(bg, 0, 0)

      // left mask like the hero gradient
      const maskGradient = ctx.createLinearGradient(0, 0, canvas.width, 0)
      maskGradient.addColorStop(0, 'rgba(0, 0, 0, 1)')
      maskGradient.addColorStop(0.05, 'rgba(0, 0, 0, 1)')
      maskGradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.46)')
      maskGradient.addColorStop(0.56, 'rgba(0, 0, 0, 0.1)')
      maskGradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
      ctx.globalCompositeOperation = 'multiply'
      ctx.fillStyle = maskGradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.globalCompositeOperation = 'source-over'

      // scale relative to screenshot proportions
      const baseWidth = 1480
      const baseHeight = 714
      const scaleX = bg.width / baseWidth
      const scaleY = bg.height / baseHeight
      const scale = Math.min(scaleX, scaleY)

      const margin = 48 * scale
      const logoW = 115 * scale
      const logoH = 51 * scale
      const logoTextW = 126 * scale
      const logoTextH = 30 * scale

      const titleSize = 48 * scale
      const pointsFontSize = 150 * scale
      const rankLabelSize = 48 * scale
      const rankFontSize = 100 * scale

      const pillHeight = 56 * scale
      const pillPaddingX = 22 * scale
      const pillRadius = pillHeight / 4

      const pawSize = 130 * scale
      const badgeSize = 180 * scale
      const defaultBadgex = margin + 260 * scale

      const userPoints = points ?? 613_252
      const userRank = rank ?? 552

      const rankName = positionToRank(userRank)
      const rankTitle = rankToTitle[rankName]
      const rankBadgeUrl = rankToBadgeUrl(rankName)
      ;(async () => {
        // load assets in parallel
        const [logo, logoText, paw, badge] = await Promise.all([
          loadImage(`${HOST}/opengraph/logo.svg`),
          loadImage(`${HOST}/opengraph/logo-text.svg`),
          loadImage(`${HOST}/opengraph/paw.svg`),
          loadImage(rankBadgeUrl),
        ])

        // logo stack
        ctx.drawImage(logo, margin, margin, logoW, logoH)
        ctx.drawImage(logoText, margin + logoW + 20 * scale, margin + 10 * scale, logoTextW, logoTextH)

        // heading
        const titleY = margin + logoH + 148 * scale
        ctx.textAlign = 'left'
        ctx.textBaseline = 'alphabetic'
        ctx.font = `600 ${Math.floor(titleSize)}px Manrope`
        ctx.fillStyle = 'rgba(255,255,255,0.5)'
        ctx.fillText('HypurrFi points', margin, titleY)

        // points row
        const pointsY = titleY + 1 * scale + pointsFontSize
        // paw icon
        ctx.drawImage(paw, margin, pointsY - pawSize + 10 * scale, pawSize, pawSize)

        // gradient for numbers
        const pointsText = userPoints.toLocaleString('en-US')
        ctx.font = `600 ${Math.floor(pointsFontSize)}px Manrope`
        const px = margin + pawSize + 28 * scale
        const pWidth = ctx.measureText(pointsText).width
        const pGrad = ctx.createLinearGradient(px + pWidth, pointsY, px, pointsY)
        pGrad.addColorStop(0, '#A1FCE7')
        pGrad.addColorStop(1, '#FCE572')
        ctx.fillStyle = pGrad
        ctx.fillText(pointsText, px, pointsY)

        // rank label
        const rankLabelY = pointsY + 64 * scale + rankLabelSize
        ctx.font = `700 ${Math.floor(rankLabelSize)}px Manrope`
        ctx.fillStyle = 'rgba(255,255,255,0.5)'
        ctx.fillText('Your rank', margin, rankLabelY)

        // rank number
        const rankY = rankLabelY + 16 * scale + rankFontSize
        ctx.font = `600 ${Math.floor(rankFontSize)}px Manrope`
        const rankText = `#${userRank}`
        const rx = margin
        const rWidth = ctx.measureText(rankText).width
        const rGrad = ctx.createLinearGradient(rx + rWidth, rankY, rx, rankY)
        rGrad.addColorStop(0, '#A1FCE7')
        rGrad.addColorStop(1, '#FCE572')
        ctx.fillStyle = rGrad
        ctx.fillText(rankText, rx, rankY)

        // badge
        const gapFromRank = 24 * scale
        const badgeX = Math.max(defaultBadgex, rx + rWidth + gapFromRank)
        const badgeY = rankY - badgeSize + 28 * scale
        ctx.drawImage(badge, badgeX, badgeY, badgeSize, badgeSize)

        // pill to the right of the badge (text only)
        ctx.font = `200 ${Math.floor(28 * scale)}px Manrope`
        const pillText = rankTitle
        const textWidth = ctx.measureText(pillText).width
        const pillX = badgeX + badgeSize + 16 * scale
        const pillY = rankY - pillHeight - 36 * scale
        const pillW = textWidth + pillPaddingX * 2

        const pillGrad = ctx.createLinearGradient(pillX + pillW, pillY, pillX, pillY)
        pillGrad.addColorStop(0, '#FCE572')
        pillGrad.addColorStop(1, '#A1FCE7')

        ctx.save()
        drawRoundedRectPath(ctx, pillX, pillY, pillW, pillHeight, pillRadius)
        ctx.fillStyle = 'rgba(255,255,255,0.06)'
        ctx.fill()
        ctx.lineWidth = 2 * scale
        ctx.strokeStyle = pillGrad
        ctx.stroke()
        ctx.restore()

        ctx.fillStyle = pillGrad
        ctx.textBaseline = 'middle'
        ctx.fillText(pillText, pillX + pillPaddingX, pillY + pillHeight / 2)

        // export
        const imageDataUrl = canvas.toDataURL('image/png')
        onImageGenerated(imageDataUrl)
      })().catch(() => {
        const imageDataUrl = canvas.toDataURL('image/png')
        onImageGenerated(imageDataUrl)
      })
    }
  }, [imageUrl, points, rank, onImageGenerated])

  return <canvas ref={canvasRef} className="hidden" />
}
